/**
 * Focus workflow routes — full-page tasks with no primary sidebar or secondary panel.
 * Register product-agnostic path suffixes here; product prefixes (`/prism`, `/design-os`, …)
 * are stripped before matching.
 */

import {
  isLibraryHubInlineAuthoring,
  normalizeLibraryPathname,
} from "@/lib/library-nav"
import { isExamLockPath } from "@/lib/exam-lock-shell"
import { isRecordDetailChromePath } from "@/lib/record-detail-chrome"

/** Canonical suffixes (no product prefix). */
export const FOCUS_WORKFLOW_PATH_SUFFIXES = [
  "/library/new",
  "/focus-workflow",
] as const

const OPTIONAL_COURSE_OFFERING_PREFIX = "(?:\\/course-offerings\\/[^/]+)?"

const ASSESSMENT_QUESTION_CREATE_PATH = new RegExp(
  `^${OPTIONAL_COURSE_OFFERING_PREFIX}\\/assessments\\/[^/]+\\/questions\\/new$`,
)

const QUESTION_IMPORT_REVIEW_PATH = /^\/question-bank\/import(\/[^/]+)?$/

const SETTINGS_CONTENT_CATEGORIES_PATH = /^\/settings\/categories$/

const ASSESSMENT_ADD_QUESTIONS_PATH = new RegExp(
  `^${OPTIONAL_COURSE_OFFERING_PREFIX}\\/assessments\\/[^/]+\\/sections\\/[^/]+\\/add-questions$`,
)

const ASSESSMENT_PREVIEW_PATH = new RegExp(
  `^${OPTIONAL_COURSE_OFFERING_PREFIX}\\/assessments\\/[^/]+\\/preview$`,
)

const ASSESSMENT_BLUEPRINT_AUTHORING_PATH = new RegExp(
  `^${OPTIONAL_COURSE_OFFERING_PREFIX}\\/assessments\\/[^/]+\\/blueprint\\/(?:new|edit)$`,
)

const ASSESSMENT_CREATE_PATH = new RegExp(
  `^${OPTIONAL_COURSE_OFFERING_PREFIX}\\/assessments\\/create$`,
)

const ASSESSMENT_DETAIL_PATH = new RegExp(
  `^${OPTIONAL_COURSE_OFFERING_PREFIX}\\/assessments\\/[^/]+$`,
)

function searchParam(search: string, key: string): string | null {
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  )
  return params.get(key)
}

/** Assessment detail composer (`?edit=1`) — same focus shell as `/library/new`. */
export function isAssessmentInlineAuthoring(
  pathname: string,
  search = "",
): boolean {
  if (searchParam(search, "edit") !== "1") return false
  return ASSESSMENT_DETAIL_PATH.test(normalizeFocusWorkflowPath(pathname))
}

function trimTrailingSlash(pathname: string): string {
  return pathname.length > 1 && pathname.endsWith("/")
    ? pathname.slice(0, -1)
    : pathname
}

/** Strip `/<product>` prefix so `/design-os/focus-workflow` → `/focus-workflow`. */
export function normalizeFocusWorkflowPath(pathname: string): string {
  const trimmed = trimTrailingSlash(pathname)
  const libraryNorm = normalizeLibraryPathname(trimmed)
  if (libraryNorm !== trimmed) return libraryNorm
  const withoutProduct = trimmed.replace(/^\/[^/]+(?=\/)/, "")
  return withoutProduct || trimmed
}

/** Add Questions keeps the primary sidebar hidden but owns a shell-level secondary rail. */
export function isAssessmentAddQuestionsPath(pathname: string): boolean {
  return ASSESSMENT_ADD_QUESTIONS_PATH.test(
    normalizeFocusWorkflowPath(pathname)
  )
}

/**
 * Add Questions keeps bank navigation on the page (`BankSidePanel` inside
 * `ListPageTreePanelShell`), not in the app shell secondary rail. Import
 * review (`?review=`) still uses the shell rail for the question list.
 */
export function assessmentAddQuestionsOwnsSecondaryPanel(
  pathname: string,
  search: string
): boolean {
  void pathname
  void search
  return false
}

const AUTHORING_SEARCH_KEYS = ["edit", "qid", "variation", "leoPrompt"] as const

/**
 * Outlet remount key that ignores compose search. `?edit=1` must not remount
 * the hub or the repaired question Leo just wrote is thrown away.
 */
export function shellOutletRemountKey(pathname: string, search = ""): string {
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  )
  for (const key of AUTHORING_SEARCH_KEYS) params.delete(key)
  const rest = params.toString()
  return `${pathname}${rest ? `?${rest}` : ""}`
}

/** Whether the current route should hide primary + secondary sidebars. */
export function isSidebarHiddenPath(pathname: string, search = ""): boolean {
  return (
    isFocusWorkflowPath(pathname) ||
    isExamLockPath(pathname) ||
    isRecordDetailChromePath(pathname) ||
    isLibraryHubInlineAuthoring(pathname, search) ||
    isAssessmentInlineAuthoring(pathname, search)
  )
}

/** Whether the current route should hide all shell sidebars (primary + secondary). */
export function isFocusWorkflowPath(pathname: string): boolean {
  const normalized = normalizeFocusWorkflowPath(pathname)
  return (
    ASSESSMENT_QUESTION_CREATE_PATH.test(normalized) ||
    ASSESSMENT_ADD_QUESTIONS_PATH.test(normalized) ||
    ASSESSMENT_PREVIEW_PATH.test(normalized) ||
    ASSESSMENT_BLUEPRINT_AUTHORING_PATH.test(normalized) ||
    ASSESSMENT_CREATE_PATH.test(normalized) ||
    QUESTION_IMPORT_REVIEW_PATH.test(normalized) ||
    SETTINGS_CONTENT_CATEGORIES_PATH.test(normalized) ||
    FOCUS_WORKFLOW_PATH_SUFFIXES.some(
      (suffix) => normalized === suffix || normalized.startsWith(`${suffix}/`)
    )
  )
}
